import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";
import {
  isPushSupported,
  getPermissionState,
  getExistingSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "../lib/push";

/**
 * Compact push-notification toggle. Renders a card with an on/off switch
 * and a "test" button when the browser is subscribed. Handles all
 * permission edge-cases (unsupported / denied / default / granted).
 *
 * Where to place: in /compte or after a successful QR checkout.
 */
export default function PushToggle({ variant = "card" }) {
  const [state, setState] = useState({
    supported: false,
    permission: "default",
    subscribed: false,
    busy: false,
  });

  const refresh = async () => {
    const supported = isPushSupported();
    if (!supported) {
      setState((s) => ({ ...s, supported: false }));
      return;
    }
    const permission = getPermissionState();
    const sub = await getExistingSubscription().catch(() => null);
    setState((s) => ({ ...s, supported, permission, subscribed: !!sub }));
  };

  useEffect(() => {
    refresh();
  }, []);

  if (!state.supported) return null;

  const enable = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      await subscribeToPush();
      toast.success("Notifications activées 🔔");
      refresh();
    } catch (e) {
      toast.error(e.message || "Impossible d'activer les notifications");
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  };

  const disable = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      await unsubscribeFromPush();
      toast.info("Notifications désactivées");
      refresh();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  };

  const test = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      const { data } = await sendTestPush();
      if (data.sent > 0) toast.success("Notification test envoyée");
      else toast.warning("Aucune notification envoyée");
    } catch {
      toast.error("Échec envoi test");
    } finally {
      setState((s) => ({ ...s, busy: false }));
    }
  };

  const denied = state.permission === "denied";
  const active = state.subscribed && state.permission === "granted";

  if (variant === "inline") {
    // Compact inline pill for post-checkout suggestion.
    if (active) return null;
    return (
      <button
        type="button"
        onClick={enable}
        disabled={state.busy || denied}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
        data-testid="push-enable-inline"
      >
        <BellRing className="w-3.5 h-3.5" />
        {denied ? "Notifications bloquées dans le navigateur" : "Activer les notifs quand ma commande est prête"}
      </button>
    );
  }

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4"
      data-testid="push-toggle-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`rounded-full p-2 ${active ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
            {active ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-primary/90 font-semibold">
              Notifications push
            </div>
            <div className="text-sm mt-0.5">
              {active
                ? "Vous recevrez une notification instantanée quand votre commande est prête au comptoir."
                : denied
                  ? "Les notifications sont bloquées. Autorisez-les dans les paramètres du navigateur."
                  : "Soyez averti sans email quand votre commande est prête au comptoir."}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {active ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={test}
                disabled={state.busy}
                className="rounded-full"
                data-testid="push-test-btn"
              >
                Tester
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={disable}
                disabled={state.busy}
                className="rounded-full text-muted-foreground hover:text-destructive"
                data-testid="push-disable-btn"
              >
                <BellOff className="w-3.5 h-3.5 mr-1.5" /> Désactiver
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={enable}
              disabled={state.busy || denied}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="push-enable-btn"
            >
              <Bell className="w-3.5 h-3.5 mr-1.5" /> {state.busy ? "…" : "Activer"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
